/**
 * Step 5: Supplementary Income
 * Add income sources beyond pensions: rental income, consulting, part-time work, etc.
 */

import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Step5SupplementaryIncomeFormData, Step5Schema, type AllStepFormData } from '../../types/wizard';
import { formatNumber, unformatNumber, planYearToCalendar } from '../../utils/formatters';
import { wizardFormKeyDown } from './wizardFormKeyDown';
import { TourButton } from '../TourGuide';
import { WIZARD_STEP5_TOUR } from '../TourGuide/tourSteps';
import './Step5SupplementaryIncome.css';

interface Step5Props {
  initialData?: Partial<AllStepFormData>;
  onNext: (data: Step5SupplementaryIncomeFormData) => void;
  onSave: (data: Step5SupplementaryIncomeFormData) => void;
  onBack?: () => void;
  lastSaved?: string;
}

function Step5SupplementaryIncome({ initialData, onNext, onSave, onBack, lastSaved }: Readonly<Step5Props>) {
  const persons: { fullName: string }[] = initialData?.persons || [];
  const isSinglePerson = persons.length <= 1;

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<Step5SupplementaryIncomeFormData>({
    resolver: zodResolver(Step5Schema),
    defaultValues: {
      supplementaryIncome: initialData?.supplementaryIncome || [],
    },
  });

  const {
    fields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'supplementaryIncome',
  });

  const watchedFields = watch('supplementaryIncome');

  // Auto-save on blur
  const handleBlur = () => {
    const currentData = watch();
    onSave(currentData);
  };

  const addIncome = () => {
    append({
      incomeId: crypto.randomUUID(),
      personIndex: 0,
      type: 'OTHER',
      description: '',
      grossAnnualAmount: 0,
      startYear: 1,
      endYear: undefined,
      inflationLinked: true,
    });
  };

  const onSubmit = (data: Step5SupplementaryIncomeFormData) => {
    onNext(data);
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const year1Total = watchedFields
    ? watchedFields
        .filter(f => f.startYear <= 1 && (!f.endYear || f.endYear >= 1))
        .reduce((sum, f) => sum + (f.grossAnnualAmount || 0), 0)
    : 0;

  return (
    <div className="step-container">
      <div className="step-header">
        <div className="step-header-row">
          <h2>Supplementary Income</h2>
          <TourButton tour={WIZARD_STEP5_TOUR} variant="text" />
        </div>
        <p>
          Add any income you expect beyond State Pension and workplace pensions &mdash; for example, rental income,
          part-time work, or consulting fees. This reduces how much you need to withdraw from your portfolio.
          Income is treated as taxable; the net benefit depends on your tax rate.
        </p>
      </div>

      <form onSubmit={(e) => e.preventDefault()} onKeyDown={wizardFormKeyDown}>
        {fields.length === 0 ? (
          <div className="empty-state">
            <p>No supplementary income added yet. This step is optional.</p>
          </div>
        ) : (
          <div className="income-list">
            {fields.map((field, index) => (
              <div key={field.id} className="income-card">
                <div className={`income-card-fields${isSinglePerson ? '' : ' has-person'}`}>
                  {isSinglePerson ? null : (
                    <div className="income-field">
                      <label htmlFor={`supp-income-person-${index}`} className="income-label">Person</label>
                      <select
                        id={`supp-income-person-${index}`}
                        className="form-input form-input-sm"
                        {...register(`supplementaryIncome.${index}.personIndex`, { valueAsNumber: true })}
                        onBlur={handleBlur}
                      >
                        {persons.map((p, pIdx) => (
                          <option key={`person-${pIdx}`} value={pIdx}>{p.fullName || `Person ${pIdx + 1}`}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="income-field">
                    <label htmlFor={`supp-income-type-${index}`} className="income-label">Type</label>
                    <select
                      id={`supp-income-type-${index}`}
                      className="form-input form-input-sm"
                      {...register(`supplementaryIncome.${index}.type`)}
                      onBlur={handleBlur}
                    >
                      <option value="OTHER">Other income</option>
                      <option value="SALARY">Salary</option>
                    </select>
                  </div>

                  <div className="income-field income-field-description">
                    <label htmlFor={`supp-income-desc-${index}`} className="income-label">Description</label>
                    <input
                      id={`supp-income-desc-${index}`}
                      type="text"
                      className="form-input form-input-sm"
                      placeholder="e.g. Rental income, Consulting"
                      {...register(`supplementaryIncome.${index}.description`)}
                      onBlur={handleBlur}
                    />
                    {errors.supplementaryIncome?.[index]?.description && (
                      <span className="error-message">{errors.supplementaryIncome[index].description?.message}</span>
                    )}
                  </div>

                  <div className="income-field">
                    <label htmlFor={`supp-income-amount-${index}`} className="income-label">Gross Annual (£)</label>
                    <Controller
                      name={`supplementaryIncome.${index}.grossAnnualAmount`}
                      control={control}
                      render={({ field }) => (
                        <input
                          id={`supp-income-amount-${index}`}
                          type="text"
                          className="form-input form-input-sm"
                          placeholder="0"
                          value={field.value === undefined || field.value === null || Number.isNaN(field.value) || field.value === 0 ? '' : formatNumber(field.value, 0)}
                          onChange={(e) => {
                            const rawValue = unformatNumber(e.target.value);
                            const numValue = Number.parseFloat(rawValue);
                            field.onChange(Number.isNaN(numValue) ? 0 : numValue);
                          }}
                          onBlur={() => {
                            field.onBlur();
                            handleBlur();
                          }}
                        />
                      )}
                    />
                    {errors.supplementaryIncome?.[index]?.grossAnnualAmount && (
                      <span className="error-message">{errors.supplementaryIncome[index].grossAnnualAmount?.message}</span>
                    )}
                  </div>

                  <div className="income-field">
                    <label htmlFor={`supp-income-start-${index}`} className="income-label">Start Year</label>
                    <div className="year-input-row">
                      <input
                        id={`supp-income-start-${index}`}
                        type="number"
                        className="form-input form-input-year"
                        min="1"
                        {...register(`supplementaryIncome.${index}.startYear`, { valueAsNumber: true })}
                        onBlur={handleBlur}
                      />
                      {initialData?.planAsOfDate && watchedFields?.[index]?.startYear && (
                        <span className="field-context-hint field-context-hint--inline">{planYearToCalendar(watchedFields[index].startYear, initialData.planAsOfDate)}</span>
                      )}
                    </div>
                    {errors.supplementaryIncome?.[index]?.startYear && (
                      <span className="error-message">{errors.supplementaryIncome[index].startYear?.message}</span>
                    )}
                  </div>

                  <div className="income-field">
                    <label htmlFor={`supp-income-end-${index}`} className="income-label">End Year</label>
                    <div className="year-input-row">
                      <input
                        id={`supp-income-end-${index}`}
                        type="number"
                        className="form-input form-input-year"
                        min="1"
                        placeholder="Indefinite"
                        {...register(`supplementaryIncome.${index}.endYear`, {
                          setValueAs: (v: string) => v === '' ? undefined : Number(v),
                        })}
                        onBlur={handleBlur}
                      />
                      {initialData?.planAsOfDate && watchedFields?.[index]?.endYear && (
                        <span className="field-context-hint field-context-hint--inline">{planYearToCalendar(Number(watchedFields[index].endYear), initialData.planAsOfDate)}</span>
                      )}
                    </div>
                    {errors.supplementaryIncome?.[index]?.endYear && (
                      <span className="error-message">{errors.supplementaryIncome[index].endYear?.message}</span>
                    )}
                  </div>

                  <div className="income-field income-field-checkbox">
                    <label className="income-checkbox-label">
                      <input
                        type="checkbox"
                        {...register(`supplementaryIncome.${index}.inflationLinked`)}
                        onChange={(e) => {
                          void register(`supplementaryIncome.${index}.inflationLinked`).onChange(e);
                          handleBlur();
                        }}
                      />
                      {' '}Rises with inflation each year
                    </label>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-delete income-delete"
                  onClick={() => { remove(index); setTimeout(handleBlur, 0); }}
                  title="Remove income source"
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          className="add-holding-btn"
          onClick={addIncome}
        >
          + Add Income Source
        </button>

        {/* Summary */}
        {watchedFields && watchedFields.length > 0 && (
          <div className="income-total">
            <strong>Year 1{initialData?.planAsOfDate ? ` (${planYearToCalendar(1, initialData.planAsOfDate)})` : ''} Gross Total:</strong> {formatCurrency(year1Total)}
          </div>
        )}

        {/* NI disclaimer — only shown when SALARY income exists */}
        {watchedFields?.some((f) => f?.type === 'SALARY') && (
          <p className="help-text" style={{ marginTop: 'var(--space-sm)' }}>
            Note: National Insurance is not modelled. Employment income before State Pension Age
            may attract NI contributions (typically 6&ndash;8% above &pound;12,570/yr).
          </p>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          {onBack && (
            <button type="button" className="btn btn-secondary" onClick={onBack}>
              Back
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={() => handleSubmit(onSubmit)()}>
            Next: Spending Plan
          </button>
          {lastSaved && (
            <span className="save-indicator">
              Saved
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

export default Step5SupplementaryIncome;
